import { useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { onTextStream } from "../../services/voiceService.js";
import {
    appendCaptionPartial,
    endCaptionTurn,
} from "../../features/voiceAssistantSlice";

export function CaptionStreamBinder() {
    const dispatch = useDispatch();
    const raf = useRef(null);
    const buf = useRef(""); // new characters since last rAF
    const carry = useRef(""); // last partial word (no boundary yet)
    const idleTimer = useRef(null); // soft boundary timer
    const IDLE_MS = 150; // tweak: 120–200ms feels snappy but safe

    const clearIdle = () => {
        if (idleTimer.current) {
            clearTimeout(idleTimer.current);
            idleTimer.current = null;
        }
    };

    useEffect(() => {
        const flush = () => {
            raf.current = null;
            const incoming = carry.current + buf.current;
            buf.current = "";

            if (!incoming) return;

            // Consider punctuation as a boundary even without a trailing space
            const endsWithBoundary = /\s$|[.!?…,)”'’]$/.test(incoming);

            // Tokenize into "word+followingSpaces"
            const tokens = incoming.match(/\S+\s*/gu) || [];

            if (tokens.length === 0) {
                // Pure whitespace—keep it in carry until we know context
                carry.current = incoming;
                return;
            }

            const lastIdx = tokens.length - 1;
            let completePart = "";

            if (endsWithBoundary) {
                // Everything is complete
                completePart = tokens.join("");
                carry.current = "";
            } else {
                // Keep last token as partial, emit the rest
                completePart = tokens.slice(0, lastIdx).join("");
                carry.current = tokens[lastIdx] || "";
            }

            if (completePart) {
                dispatch(appendCaptionPartial(completePart));
            }
        };

        // Soft flush: if no new partials for a short time, emit carry anyway
        const softFlushCarry = () => {
            idleTimer.current = null;
            if (carry.current) {
                dispatch(appendCaptionPartial(carry.current));
                carry.current = "";
            }
        };

        const unsub = onTextStream((evt) => {
            if (evt.type === "partial") {
                // Reset soft boundary timer on each partial
                clearIdle();
                buf.current += evt.text || "";

                // Schedule main flush to tokenize/emit
                if (!raf.current) raf.current = requestAnimationFrame(flush);

                // Start soft boundary timer — if no more chars come in, push the last word
                idleTimer.current = setTimeout(softFlushCarry, IDLE_MS);
            } else {
                // Final/Interrupted/Turn-complete → emit everything immediately
                if (raf.current) {
                    cancelAnimationFrame(raf.current);
                    raf.current = null;
                }
                clearIdle();

                const finalChunk = (carry.current || "") + (buf.current || "");
                carry.current = "";
                buf.current = "";

                if (finalChunk) dispatch(appendCaptionPartial(finalChunk));
                dispatch(endCaptionTurn());
            }
        });

        return () => {
            unsub();
            if (raf.current) cancelAnimationFrame(raf.current);
            clearIdle();
        };
    }, [dispatch]);

    return null;
}
